import { useEffect, useMemo, useCallback, useRef } from 'react';
import { useAppContext } from '../store';
import { NgapAct } from '../types/ngap';
import { validateSearchInput } from '../utils/inputValidation';
import { trackSearchSubmitted, trackSearchNoResult } from '../utils/analyticsEvents';
import { logger } from '../utils/logger';

interface UseSearchReturn {
  filteredActs: NgapAct[];
  searchQuery: string;
  handleSearchChange: (value: string) => void;
  handleSearchSubmitted: (value: string) => void;
}

export const useSearch = (): UseSearchReturn => {
  const { state, dispatch } = useAppContext();
  const { acts } = state.data;
  const { searchQuery, filters } = state.searchAndFilter;
  
  // Référence pour la valeur actuelle de searchQuery (pour éviter les re-créations de fonctions)
  const searchQueryRef = useRef(searchQuery);
  
  logger.debug('🔎 useSearch hook start', {
    searchQuery,
    actsCount: acts.length,
    filtersActive: Object.values(filters).some(filter => 
      Array.isArray(filter) ? filter.length > 0 : filter !== null && filter !== undefined
    ),
    timestamp: new Date().toISOString()
  });
  
  // Synchroniser la référence avec la valeur actuelle
  useEffect(() => {
    logger.debug('🔄 useSearch searchQueryRef sync', {
      previousRef: searchQueryRef.current,
      newValue: searchQuery,
      timestamp: new Date().toISOString()
    });
    searchQueryRef.current = searchQuery;
  }, [searchQuery]);
  
  // Filter and search logic
  const filteredActs = useMemo(() => {
    logger.debug('🔍 useSearch filteredActs calculation started', {
      searchQuery,
      actsCount: acts.length,
      filtersSnapshot: {
        theme: filters.theme?.length || 0,
        reeducation: filters.reeducation?.length || 0,
        soumisReferentiel: filters.soumisReferentiel,
        lettresCles: filters.lettresCles?.length || 0
      }
    });
    
    let result = [...acts];

    // Apply search query
    if (searchQuery.trim()) {
      logger.debug('🔎 useSearch applying search filter', {
        query: searchQuery,
        beforeCount: result.length
      });
      
      const query = searchQuery.toLowerCase().trim();
      
      result = result.filter(act => {
        const searchableText = [
          act.libelle,
          act.lettre_cle,
          act.cotation_valeur,
          act.theme,
          act.reeducation,
          act.sous_division,
          act.pathologies,
          act.vignette_label_4
        ].filter(Boolean).join(' ').toLowerCase();
        
        return searchableText.includes(query);
      });
      
      logger.debug('🔎 useSearch search filter applied', {
        query: searchQuery,
        resultCount: result.length
      });
    } else {
      logger.debug('🔎 useSearch no search query, keeping all acts');
    }

    // Apply filters
    result = result.filter(act => {
      // Letter key filter
      if (filters.lettre_cle && act.lettre_cle?.toUpperCase() !== filters.lettre_cle.toUpperCase()) {
        return false;
      }

      // Theme filter
      if (filters.theme && act.theme !== filters.theme) {
        return false;
      }

      // Reeducation filter
      if (filters.reeducation && act.reeducation !== filters.reeducation) {
        return false;
      }

      // Referentiel filter
      if (filters.soumis_referentiel !== 'all') {
        const isReferentiel = filters.soumis_referentiel === 'true';
        if (act.soumis_referentiel !== isReferentiel) {
          return false;
        }
      }

      // Vignettes filter - MODIFIED TO USE "AND" LOGIC
      if (filters.selected_vignettes && filters.selected_vignettes.length > 0) {
        const actVignettes = new Set([
          act.vignette_label_0,
          act.vignette_label_1,
          act.vignette_label_2,
          act.vignette_label_3,
          act.vignette_label_4
        ].filter(Boolean)); // Filter out null/undefined values and create a Set for efficient lookup

        // Check if the act has ALL selected vignettes (AND logic)
        const hasAllSelectedVignettes = filters.selected_vignettes.every(selectedVignette => 
          actVignettes.has(selectedVignette)
        );
        
        if (!hasAllSelectedVignettes) {
          return false; // Exclude the act if it doesn't have all selected vignettes
        }
      }

      return true;
    });

    logger.debug('✅ useSearch filteredActs calculation complete', {
      finalCount: result.length,
      totalProcessingSteps: [
        searchQuery ? 'search' : null,
        filters.theme?.length ? 'theme' : null,
        filters.reeducation?.length ? 'reeducation' : null,
        filters.soumisReferentiel !== null ? 'soumisReferentiel' : null,
        filters.lettresCles?.length ? 'lettresCles' : null
      ].filter(Boolean).length
    });
    
    return result;
  }, [acts, searchQuery, filters, state.searchAndFilter.cartItems]);

  // Fonction de recherche dynamique (sans debounce)
  const handleSearchChange = useCallback((value: string) => {
    logger.debug('🔄 useSearch handleSearchChange called', {
      receivedValue: value,
      currentSearchQuery: searchQueryRef.current,
      timestamp: new Date().toISOString()
    });
    
    // Valider l'entrée
    const validation = validateSearchInput(value);
    logger.debug('✅ useSearch validation result', validation);
    
    // Vérifier si la valeur a réellement changé avant de dispatcher
    if (validation.sanitizedValue === searchQueryRef.current) {
      logger.debug('🚫 useSearch skipping dispatch - value unchanged', {
        sanitizedValue: validation.sanitizedValue,
        currentValue: searchQueryRef.current
      });
      return;
    }
    
    logger.debug('⚡ useSearch dispatching SET_SEARCH_QUERY', {
      newValue: validation.sanitizedValue,
      previousValue: searchQueryRef.current
    });
    
    dispatch({
      type: 'SET_SEARCH_QUERY',
      payload: validation.sanitizedValue
    });
  }, [dispatch]); // Suppression de searchQuery des dépendances

  const handleSearchSubmitted = useCallback((value: string) => {
    logger.debug('🚀 useSearch handleSearchSubmitted called', {
      submittedValue: value,
      currentSearchQuery: searchQueryRef.current,
      timestamp: new Date().toISOString()
    });
    
    const validation = validateSearchInput(value);
    logger.debug('✅ useSearch submission validation result', validation);
    const sanitizedValue = validation.sanitizedValue.trim();
    
    // Mettre à jour lors de la soumission
    dispatch({ type: 'SET_SEARCH_QUERY', payload: sanitizedValue });
    logger.debug('🚀 useSearch dispatching SET_SEARCH_QUERY immediately');
    
    // Reset to first page when search is submitted
    dispatch({ type: 'SET_CURRENT_PAGE', payload: 1 });
    logger.debug('🚀 useSearch resetting to page 1 after search submission');
    
    if (validation.isValid && sanitizedValue) {
      logger.debug('📝 useSearch adding to recent searches', {
        value: sanitizedValue
      });
      
      // Ajouter à l'historique des recherches récentes
      dispatch({
        type: 'ADD_RECENT_SEARCH',
        payload: sanitizedValue
      });
      
      logger.debug('⚡ useSearch dispatching search query update', {
        value: validation.sanitizedValue
      });
      
      // Mettre à jour la requête de recherche
      dispatch({
        type: 'SET_SEARCH_QUERY',
        payload: validation.sanitizedValue
      });
    } else {
      logger.debug('❌ useSearch submission validation failed', {
        value,
        validation
      });
    }
    
    // Track search
    if (sanitizedValue) {
      trackSearchSubmitted(sanitizedValue);
      const matchCount = acts.filter(a =>
        (a.libelle || '').toLowerCase().includes(sanitizedValue.toLowerCase()) ||
        (a.lettre_cle || '').toLowerCase().includes(sanitizedValue.toLowerCase())
      ).length;
      if (matchCount === 0) {
        trackSearchNoResult(sanitizedValue);
      }
    }
  }, [dispatch, acts]);

  return {
    filteredActs,
    searchQuery,
    handleSearchChange,
    handleSearchSubmitted
  };
};